import React, { useEffect, useRef, useState } from "react";
import {
  ArrowUpRight,
  Plus,
} from "lucide-react";

import Service1 from "../assets/Service/Service1.png";
import Service2 from "../assets/Service/Service2.png";
import Service3 from "../assets/Service/service3.png";
import Service4 from "../assets/Service/service4.png";
import "./Services.css";

function Services() {
  const sectionRef = useRef(null);

  const serviceGroups = [
    {
      id: "01",
      title: "BUILDING THE BRAND",
      subtitle: "Brand Strategy & Operations",
      icon: Service1,
      intro:
        "We give your brand a voice people recognise, a story people remember, and a culture people want to belong.",
      services: [
        {
          title: "Brand Strategy Development",
          text: "The best campaigns don't begin with content. They begin with conversations. Before we create, we listen. Sometimes I wonder if anyone actually reads these paragraphs. If you do, you're now part of a very exclusive club. We understand your founder's journey, we challenge assumptions, uncover cultural insights, and define the story your brand should own. Everything else, from design to content to performance, is simply built on that foundation.",
        },
        {
          title: "Visual Identity Design & Brand Guidelines",
          text: "Before people trust your story, they recognise your face. We turn your brand's story into a visual language that's impossible to mistake. I should probably be writing this section. Instead, I'm wondering who decided that \"Helvetica\" sounds like a disease but is actually a font. From colours and typography to complete brand systems, we make sure every touchpoint feels like it belongs to the same conversation. Because brands don't become culture by looking different every day, they become culture by showing up consistently enough to be remembered.",
        },
        {
          title: "Personal & Founder Branding",
          text: "People don't have chai breaks to discuss logos. They talk about people. Today's founders aren't just building companies. Sometimes I wonder if Shakespeare would've had a LinkedIn profile. Probably. He would've absolutely posted \"Words matter.\" They're storytellers, thought leaders, and personalities shaping conversations far beyond their businesses. They're quoted in boardrooms, discussed over chai, amplified through podcasts, invited to panel discussions, and remembered as much for their ideas as the companies they've built. I just caught myself practising how this paragraph would sound if Morgan Freeman narrated it. Now you will read the next line in Morgan Freeman's voice. Job done. We help founders find their voice, shape their story, and show up consistently, so every post, interview, keynote, and conversation feels unmistakably them. Because before people buy into a company, they buy into the person building it.",
        },
        {
          title: "Social Media Management",
          text: "Brands aren't built in one viral post. They're built over hundreds of conversations. Every reel, carousel, comment, and caption is another chance to remind people who you are. Remember when \"going viral\" meant catching a cold? Simpler times. We help your brand show up consistently with stories that connect, conversations that continue, and content that people actually remember long after they've scrolled past. The reels might get the views, but deep down I know it's my captions carrying the whole TCC's Insta account. At least that's what I tell myself every Monday.",
        },
        {
          title: "Community Management",
          text: "The feed gets the attention. The comments get the gossip. The DMs build the relationship. Sorry, I got distracted scrolling through Comet's Instagram again. Their sneakers are great. But I feel like I belong in their universe. Just don't hit me with a Comet. We make sure your brand shows up to every conversation with the same voice, the same personality, and the same warmth, whether it's a story reply, a comment, or an appreciation DM. Because people don't remember brands that only know how to post. They remember the ones that know how to talk. This paragraph is about community management. I think asking you to visit our Instagram is technically community management. See? I'm good. I should get a raise.",
        },
        {
          title: "Website Design & Development",
          text: "Your website is often the first conversation people have with your brand. Before they hear your pitch, they experience your story. Every agency website says they're \"different.\" We proved it. Just remember how we made you feel. My appraisal depends on it. Please feel different. Just smile. We build websites that make every scroll feel intentional, every interaction feel effortless, and every page move people one step closer to saying, \"These are my people.\" We are your people.",
        },
      ],
    },

    {
      id: "02",
      title: "THE STOR(E)Y ROOM",
      subtitle: "Content Production & Storytelling",
      icon: Service2,
      intro:
        "Feeds move fast. Stories move people. We create films, visuals, podcasts, and branded content that make people pause, feel something, and carry the conversation forward. Because the best stories don't chase attention. They earn a place in people's lives.",
      services: [
        {
          title: "Advertising & Brand Content",
          text: "People don't skip ads because they're ads. They skip them because they don't care. We create campaigns that feel less like interruptions and more like stories people choose to watch, share, and talk about. I like to think I have good taste in music. My colleagues disagree because half the songs I hum were written to sell toothpaste and soft drinks. What do they know? Unke toothpaste me to namak hai. Every idea begins with understanding what your brand stands for, then bringing it to life with thoughtful direction, strong production, and storytelling that stays long after the screen goes dark.",
        },
        {
          title: "Television Commercials (TVCs)",
          text: "A camera can capture a moment. A story can capture attention. We combine both to create TVCs that feel cinematic, purposeful, and worth watching. Sometimes I forget I'm supposed to be watching a cricket match. I'm too busy wondering who directed the ad during the drinks break. My therapist calls that a niche hobby. From the first idea to the final frame, every decision is made with one question in mind: Will people still be talking about this after the commercial ends?",
        },
        {
          title: "Digital Advertisements",
          text: "Algorithms decide what people see. Stories decide what they remember. That Skip Ad button on YouTube has given my index finger an identity crisis. Sometimes the ad is so good that I don't click it, like Finger not-clickin' good. We create digital ads that are built for the way people actually scroll, watch, and engage, capturing attention in seconds and giving them a reason to stick around. Because stopping the scroll is only half the job. Staying in their mind is the rest.",
        },
        {
          title: "Brand Films",
          text: "People don't build relationships with brands because they know what they do. They build them because they understand why they do it. We create brand films that uncover that 'why' and bring it to life through stories that feel honest, cinematic, and deeply human. Being a copywriter is weird. One minute I'm debating commas. The next I'm crying because a cement company made a beautiful film about hope. Because the brands people remember aren't always the loudest. They're the ones that made them feel something.",
        },
        {
          title: "Corporate AVs",
          text: "Numbers explain what you've achieved. Stories explain why it matters. Corporate AVs have an unfair reputation. It's like saying all PowerPoint presentations are boring. Okay... bad example. But Corporate AVs can be cinematic too. That's what I wanted to say. We create corporate AVs that transform business milestones into films people actually want to watch, combining strategy, storytelling, and cinematic execution to make every message feel human, memorable, and impossible to overlook.",
        },
        {
          title: "Documentary Films",
          text: "Some stories don't need to be dramatised. They just need to be told honestly. We uncover the people, purpose, and moments that define your journey, then shape them into documentary films that feel authentic, emotional, and deeply human. Every time I watch a good documentary, I become briefly convinced I should quit my job and dedicate my life to whatever it's about. What's life about but? What's the point of life? Pointless life. This job is my only hope. Hope I am not fired in this bad economy for all these interruptions. Because the most powerful stories aren't the ones we create. They're the ones we choose to tell truthfully.",
        },
        {
          title: "Podcast Production",
          text: "Some stories need more than thirty seconds. Some conversations deserve more than a caption. Podcasts give ideas the time they need to breathe, challenge, inspire, and connect. I listen to podcasts at 1.75x speed. And my manager says I am a slow learner. Duh! We help you build a show that's rooted in thoughtful conversations and brought to life through seamless production, so every episode doesn't just fill an hour, it earns a place in people's routines. Because culture has always been built one conversation at a time.",
        },
        {
          title: "Photography",
          text: "People don't stop scrolling because a photograph is beautiful. They stop because it makes them feel something. Every image your brand puts into the world is telling a story, whether you intended it to or not. We make sure it's the right one. From campaign shoots and product photography to founder portraits and brand imagery, we create visuals that feel intentional, unmistakably yours, and impossible to confuse with anyone else's. Our co-founder keeps saying, \"We have ten minutes of golden hour left.\" I keep hearing, \"There goes my evening.\" She is also a photographer. Because before people read your story, they see it. And our Co-founder is a photographer herself, so you don't need to be stressed about good photography from our side at all.",
        },
        {
          title: "Event Coverage & After Movies",
          text: "The success of an event isn't measured only by the day itself, but by the story it leaves behind. I have a talent for appearing in behind-the-scenes footage at exactly the wrong moment. Thankfully, our editors are even more talented. They keep saying what was a copy intern doing at events? Our Co-founder is there at all those events. He is also a writer. Why don't they question him? From the excitement of the crowd to the smallest behind-the-scenes moments, we document every detail with intention and craft after movies that preserve the experience in its truest form.",
        },
      ],
    },

    {
      id: "03",
      title: "THE CONVERSION CORNER",
      subtitle: "Growth & Performance",
      icon: Service3,
      intro:
        "The best stories know when to turn the page. Culture is constantly evolving, and your brand should evolve with it. We combine creativity with data, intuition with insights, and storytelling with performance to make sure every campaign keeps earning its place in the conversation.",
      services: [
        {
          title: "Performance Marketing",
          text: "Performance marketing is about more than just clicks and conversions, it's about creating campaigns that drive meaningful business growth. Somewhere, an ad I wrote is being A/B tested against another version I also wrote. I'm literally competing against myself. I have no competition. All this BS about world being cut-throat and all. Peace. We combine audience insights, compelling creatives, and continuous optimisation to ensure every campaign reaches the right people, delivers measurable results, and maximises every marketing investment.",
        },
        {
          title: "Meta & Google Advertising",
          text: "People don't buy because they saw your ad once. They buy because your brand keeps showing up with the right story. Sometimes I wonder if I'm making decisions... or just completing someone else's ad funnel. Oh I was competing against that guy! Humbled again. That's why every campaign is constantly tested, refined, and improved. So when your audience is finally ready to make a decision, your brand is already familiar.",
        },
        {
          title: "Lead Generation Campaigns",
          text: "Not every lead is your customer. And that's perfectly okay. We'd rather help you find the people who genuinely need what you offer than fill your CRM with names that go nowhere. I used to think rejection built character. Then I looked at an unqualified leads report. Turns out it also builds spreadsheets. Through smart targeting, thoughtful messaging, and constant optimisation, we bring you conversations that have a real chance of becoming customers.",
        },
        {
          title: "Influencer Marketing",
          text: "The best recommendations don't sound like ads. They sound like a friend saying, \"You should try this.\" That's exactly how we approach influencer marketing. My toxic trait is believing I'm researching influencer marketing while accidentally becoming the target audience. We partner your brand with creators who genuinely fit your story, so every collaboration feels authentic, builds trust, and inspires people to choose you, not because they were sold to, but because someone they trust believed in you first.",
        },
        {
          title: "Marketing Strategy & Funnel Planning",
          text: "Marketing isn't a collection of campaigns. It's one long conversation. Every time I think I've finally understood marketing, someone mentions attribution models and I quietly pretend my internet is lagging. We make sure every platform is saying the same thing, in the right way, to the right people. Because when your story stays consistent, your audience knows exactly who you are, and why they should buy from you.",
        },
      ],
    },
  ];

  const [activeService, setActiveService] = useState(serviceGroups[0].id);
  const [openItem, setOpenItem] = useState(`${serviceGroups[0].id}-0`);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const elements =
            sectionRef.current.querySelectorAll(".service-reveal");

          elements.forEach((element) => {
            element.classList.add("active");
          });

          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const activeGroup =
    serviceGroups.find((group) => group.id === activeService) ||
    serviceGroups[0];

  const handleGroupSelect = (groupId) => {
    setActiveService(groupId);
    setOpenItem(`${groupId}-0`);
  };

  const handleItemToggle = (itemKey) => {
    setOpenItem((current) => (current === itemKey ? null : itemKey));
  };

  return (
    <section id="services" className="services" ref={sectionRef}>
      <div className="services-container">
        {/* ================= HEADER ================= */}

        <div className="services-heading service-reveal">
          <div className="services-eyebrow">
            <span></span>
            THE CULTURE KIT
            <span></span>
          </div>

          <h2>
            Everything your brand
            <em> needs to matter.</em>
          </h2>

          <p>
            From strategy and storytelling to growth and performance, we
            build brands that earn their place in culture.
          </p>
        </div>

        {/* ================= SERVICE EXPERIENCE ================= */}

        <div className="services-experience service-reveal">
          {/* ================= TOP JOURNEY ================= */}

          <div className="services-journey" role="tablist">
            {serviceGroups.map((group, index) => {
              const isActive = activeService === group.id;

              return (
                <React.Fragment key={group.id}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`service-node ${isActive ? "is-active" : ""}`}
                    onMouseEnter={() => handleGroupSelect(group.id)}
                    onFocus={() => handleGroupSelect(group.id)}
                    onClick={() => handleGroupSelect(group.id)}
                  >
                    {/* ICON */}

                    <div className="service-icon-area">
                      <div className="service-icon-ring">
                        <div className="service-icon">
                          <img
                            src={group.icon}
                            alt={`${group.title} icon`}
                            className="custom-service-icon"
                          />
                        </div>

                        <div className="service-icon-arrow">
                          <ArrowUpRight size={14} />
                        </div>
                      </div>
                    </div>

                    {/* NUMBER */}

                    <span className="service-node-number">{group.id}</span>

                    {/* TITLE */}

                    <h3>{group.title}</h3>

                    {/* SUBTITLE */}

                    <p>{group.subtitle}</p>
                  </button>

                  {/* CONNECTING DOTS */}

                  {index < serviceGroups.length - 1 && (
                    <div className="service-connector">
                      {Array.from({ length: 10 }).map((_, dotIndex) => (
                        <span key={dotIndex}></span>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* ================= ACTIVE SERVICE PANEL ================= */}

          <div className="service-panel panel-visible" key={activeGroup.id}>
            <div className="service-panel-inner">
              {/* PANEL HEADER */}

              <div className="service-panel-header">
                <div>
                  <span className="panel-number">{activeGroup.id}</span>

                  <h4>{activeGroup.title}</h4>

                  <p>{activeGroup.intro}</p>
                </div>

                <div className="panel-line"></div>
              </div>

              {/* SERVICES ACCORDION */}

              <div className="service-accordion">
                {activeGroup.services.map((service, index) => {
                  const itemKey = `${activeGroup.id}-${index}`;
                  const isOpen = openItem === itemKey;

                  return (
                    <div
                      className={`accordion-item ${
                        isOpen ? "is-open" : ""
                      }`}
                      key={itemKey}
                    >
                      <button
                        type="button"
                        className="accordion-trigger"
                        aria-expanded={isOpen}
                        onClick={() => handleItemToggle(itemKey)}
                      >
                        <span className="accordion-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span className="accordion-title">
                          {service.title}
                        </span>

                        <span className="accordion-icon">
                          <Plus size={16} strokeWidth={1.5} />
                        </span>
                      </button>

                      <div className="accordion-body">
                        <div className="accordion-body-inner">
                          <p>{service.text}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Services;